import React from 'react';
import { RED, INK, GREY, LIGHT, TEAL, BORDER, FONT, wrap, btnRed, Hero, Crumbs, CtaBand } from '../seo/SeoUI';
import { serviceContentFor } from './serviceContent';
import { cityContentFor } from './cityContent';
import { pillarCityBySlug } from '../../core/seo/pillarCities';

function SeoLanding({ page }) {
  const service = page.serviceName || 'Home services';
  const city = page.cityName || '';
  const place = city ? `${city}, ${page.stateCode}` : '';
  const lc = service.toLowerCase();

  // Rich service explanation (Paul): weave the city into the stored {city} token — "in New York" on a
  // city page, "near you" on the hub. Falls back to the generated introHtml when no description is set.
  const cityLabel = city ? `in ${city}` : 'near you';
  const serviceDesc = page.serviceDescription ? page.serviceDescription.replace(/\{city\}/g, cityLabel) : null;
  const heroImage = page.serviceImage ? `/images/category/${page.serviceImage}` : null;

  // B2.1: genuinely service-specific content (unique FAQ + "about") so each service reads differently —
  // the doorway-page fix. Falls back to the generic cost/timing FAQ for services without a tailored entry.
  const sc = serviceContentFor(page.serviceSlug);
  const fill = (s) => (s || '').replace(/\{city\}/g, city || 'your area');
  // Regional focus: genuinely unique local content (real neighborhoods, ZIPs, context) for the target
  // cities — the de-duplication lever. {service} is filled with the lowercase service name.
  const cc = cityContentFor(page.citySlug);
  const pillarCity = pillarCityBySlug(page.citySlug); // up-link to the city Home Services pillar (maillage)
  const fillS = (s) => (s || '').replace(/\{service\}/g, lc);
  const ccHoods = (cc && cc.neighborhoods) || [];
  // Cost guide + city FAQ: use a city's hand-written copy when present, else generate from the city +
  // its real neighborhoods (so every target city gets the section, filled with its own local detail).
  const ccCostText = cc ? (cc.costIntro ? fillS(cc.costIntro) : `Costs for ${lc} in ${city} depend on the size of the job and how quickly you need it. BestHand shows you an upfront price for your specific ${lc} job before you book — no hourly surprises and no lead fees.`) : null;
  const ccFaqs = cc
    ? (cc.faqs
        ? cc.faqs.map(([q, a]) => ({ q: fillS(q), a: fillS(a) }))
        : [
            { q: `Which ${city} neighborhoods does BestHand cover for ${lc}?`, a: `BestHand covers ${city}${ccHoods.length ? ` — including ${ccHoods.slice(0, 6).join(', ')} and more` : ''}, plus nearby areas. Enter your address and we match you with a nearby ${lc} pro.` },
            { q: `How fast can I get a ${lc} pro in ${city}?`, a: `Most ${city} ${lc} jobs can be scheduled within a few days, with same-week availability from many local pros.` },
          ])
    : [];
  const serviceAbout = sc && sc.about ? fill(sc.about) : null;
  const faqs = [
    ...(sc ? sc.faqs.map(([q, a]) => ({ q: fill(q), a: fill(a) })) : [
      { q: `How much does ${lc} cost in ${city}?`, a: `${lc.charAt(0).toUpperCase() + lc.slice(1)} pricing in ${city} depends on the size and scope of the job. On BestHand you see upfront pricing before you book — no surprises.` },
      { q: `How soon can I book ${lc} in ${city}?`, a: `Most jobs in ${place} can be scheduled within a few days, and many pros offer same-week availability.` },
    ]),
    // City-specific FAQ (regional focus) — hand-written per city or generated from its neighborhoods.
    ...ccFaqs,
    // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
    { q: `Are ${lc} pros on BestHand verified?`, a: `Yes. Every pro completes a background check, and you can read real customer reviews before you book.` },
    { q: `Is my payment protected?`, a: `Payments are handled securely in-app and backed by our satisfaction guarantee, so you only pay for work done right.` },
  ];
  const steps = [
    { t: 'Tell us what you need', d: `Describe your ${lc} job in ${city} in a minute.` },
    { t: 'Get matched with a pro', d: 'Compare vetted, reviewed local pros and upfront pricing.' },
    { t: 'Book & relax', d: 'Schedule, pay securely in-app, and enjoy guaranteed work.' },
  ];

  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'Service', serviceType: service, areaServed: { '@type': 'City', name: city, address: { '@type': 'PostalAddress', addressRegion: page.stateCode, addressCountry: page.countryCode || 'US' } }, provider: { '@type': 'Organization', name: 'BestHand' }, description: page.metaDescription },
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [ { '@type': 'ListItem', position: 1, name: 'Home', item: '/' }, { '@type': 'ListItem', position: 2, name: service, item: `/services/${page.serviceSlug}` }, { '@type': 'ListItem', position: 3, name: place, item: page.path } ] },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div style={{ fontFamily: FONT, color: INK, background: '#fff' }}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth; fields are admin-authored). */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }} />

      <Hero
        breadcrumb={<Crumbs items={[{ label: 'Home', href: '/' }, { label: service, href: `/services/${page.serviceSlug}` }, { label: place }]} />}
        eyebrow={place}
        title={page.h1}
        subtitle={page.serviceTagline || /* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */ `Trusted, background-checked ${lc} pros near you.`}
        badges={[/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */ 'Background-checked pros', 'Upfront pricing', 'Secure in-app payment', 'Satisfaction guarantee']}
        image={heroImage}
        imageAlt={`${service} ${place}`.trim()}
      >
        <a href="/category?type=all" style={btnRed}>Get a free estimate</a>
      </Hero>

      {/* Subcategory buttons (Paul): the category's active subcategories, each a button into the normal
          booking flow for this service. Dynamic — 1 or many, admin-managed. Hidden if there are none. */}
      {page.subCategoryNames && page.subCategoryNames.length > 0 && page.serviceBookingHref ? (
        <section style={{ ...wrap, padding: '28px 20px 0' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEAL, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>What you can book</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {page.subCategoryNames.map((name) => (
              <a key={name} href={page.serviceBookingHref} style={{ display: 'inline-block', padding: '10px 16px', borderRadius: 999, border: `1px solid ${BORDER}`, background: '#fff', color: INK, fontWeight: 600, fontSize: 15, textDecoration: 'none' }}>
                {name} <span aria-hidden="true" style={{ color: RED }}>→</span>
              </a>
            ))}
          </div>
        </section>
      ) : null}

      <section style={{ ...wrap, padding: '48px 20px 8px' }}>
        {serviceAbout ? (
          <p style={{ fontSize: 18, lineHeight: 1.75, color: '#1F2733', maxWidth: 820, margin: '0 0 18px', fontWeight: 500 }}>{serviceAbout}</p>
        ) : null}
        {serviceDesc ? (
          <div style={{ fontSize: 17, lineHeight: 1.7, color: '#374151', maxWidth: 820 }}>
            <p style={{ margin: '0 0 16px' }}>{serviceDesc}</p>
            {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
            <p style={{ margin: 0 }}>Every BestHand pro is background-checked and reviewed by real customers — with upfront pricing, secure in-app payment, and a satisfaction guarantee.</p>
          </div>
        ) : (
          <div style={{ fontSize: 17, lineHeight: 1.7, color: '#374151', maxWidth: 820 }} dangerouslySetInnerHTML={{ __html: page.introHtml || '' }} />
        )}
      </section>

      {cc ? (
        <section style={{ ...wrap, padding: '8px 20px 12px' }}>
          <p style={{ fontSize: 17, lineHeight: 1.75, color: '#374151', maxWidth: 820, margin: '0 0 18px' }}>{fillS(cc.context)}</p>
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: '10px 0 12px' }}>{service} across {city}</h2>
          <p style={{ fontSize: 16, color: '#374151', maxWidth: 820, lineHeight: 1.7, margin: '0 0 14px' }}>
            BestHand covers {city} neighborhood to neighborhood — including {cc.neighborhoods.slice(0, -1).join(', ')} and {cc.neighborhoods.slice(-1)}
            {cc.zips && cc.zips.length ? `, and ZIP codes ${cc.zips.slice(0, 8).join(', ')} and more` : ''}.
          </p>
          {cc.localNote ? <p style={{ fontSize: 16, color: GREY, maxWidth: 820, lineHeight: 1.7, margin: 0 }}>{fillS(cc.localNote)}</p> : null}
          {ccCostText ? (
            <>
              <h2 style={{ fontSize: 24, fontWeight: 800, margin: '26px 0 12px' }}>How much does {lc} cost in {city}?</h2>
              <p style={{ fontSize: 16, color: '#374151', maxWidth: 820, lineHeight: 1.7, margin: 0 }}>{ccCostText}</p>
            </>
          ) : null}
        </section>
      ) : null}

      <section style={{ ...wrap, padding: '36px 20px' }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 24px' }}>How BestHand works in {city}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ background: LIGHT, borderRadius: 16, padding: '26px 22px', border: `1px solid ${BORDER}` }}>
              <div style={{ width: 42, height: 42, borderRadius: 999, background: i === 1 ? TEAL : RED, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, marginBottom: 14 }}>{i + 1}</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{s.t}</h3>
              <p style={{ fontSize: 15, color: GREY, margin: 0, lineHeight: 1.6 }}>{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WORKER SEO — capture pro/company intent on every service×city page. */}
      <section style={{ background: LIGHT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ ...wrap, padding: '44px 20px', display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: '1 1 360px', minWidth: 280 }}>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: RED, marginBottom: 8 }}>For pros &amp; companies</div>
            <h2 style={{ fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 800, margin: '0 0 8px', color: INK, lineHeight: 1.1 }}>Are you a {lc} pro in {city || 'your area'}?</h2>
            <p style={{ color: GREY, lineHeight: 1.55, margin: 0, maxWidth: '42em' }}>Get {lc} jobs near you{place ? ` in ${place}` : ''}, set your own schedule, keep your rate, and get paid fast — free to join, no lead fees. Companies can take on larger projects with milestone-escrow payouts.</p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a href="/become-a-pro" style={btnRed}>Become a pro →</a>
            <a href="/become-a-company" style={{ ...btnRed, background: '#fff', color: INK, border: `1px solid ${BORDER}` }}>Join as a company</a>
          </div>
        </div>
      </section>

      <section style={{ background: LIGHT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ ...wrap, padding: '46px 20px' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 22px' }}>{service} in {city} — frequently asked questions</h2>
          <div style={{ display: 'grid', gap: 14 }}>
            {faqs.map((f, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', border: `1px solid ${BORDER}` }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 8px' }}>{f.q}</h3>
                <p style={{ fontSize: 15, color: GREY, margin: 0, lineHeight: 1.6 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {page.neighborhoods && page.neighborhoods.length > 0 ? (
        <section style={{ background: LIGHT, borderTop: `1px solid ${BORDER}` }}>
          <div style={{ ...wrap, padding: '36px 20px' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 18px' }}>{service} in {city} neighborhoods</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {page.neighborhoods.map((r) => (
                <a key={r.path} href={r.path} style={{ display: 'inline-block', padding: '9px 15px', borderRadius: 999, border: `1px solid ${BORDER}`, background: '#fff', color: INK, fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>{service} in {r.name}</a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {page.relatedServices && page.relatedServices.length > 0 ? (
        <section style={{ ...wrap, padding: '36px 20px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 18px' }}>Other home services in {city}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {page.relatedServices.map((r) => (
              <a key={r.path} href={r.path} style={{ display: 'inline-block', padding: '9px 15px', borderRadius: 999, border: `1px solid ${BORDER}`, background: '#fff', color: INK, fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>{r.name} in {city}</a>
            ))}
          </div>
        </section>
      ) : null}

      {pillarCity ? (
        <section style={{ ...wrap, padding: '0 20px 8px' }}>
          <a href={`/home-services/${pillarCity.slug}`} style={{ color: RED, fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>See all home services in {pillarCity.name} →</a>
        </section>
      ) : null}

      {page.relatedCities && page.relatedCities.length > 0 ? (
        <section style={{ background: LIGHT, borderTop: `1px solid ${BORDER}` }}>
          <div style={{ ...wrap, padding: '36px 20px' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 18px' }}>{service} in other cities</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {page.relatedCities.map((r) => (
                <a key={r.path} href={r.path} style={{ display: 'inline-block', padding: '9px 15px', borderRadius: 999, border: `1px solid ${BORDER}`, background: '#fff', color: INK, fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>{service} in {r.name}</a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section style={{ ...wrap, padding: '4px 20px 36px' }}>
        <a href="/compare/best-handyman-app" style={{ color: RED, fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>See why BestHand is a top alternative to TaskRabbit, Thumbtack &amp; Angi →</a>
      </section>

      <CtaBand title={`Ready to book ${lc} in ${place}?`} subtitle="Get matched with a trusted local pro in minutes." ctaText="Get started" ctaHref="/category?type=all" />
    </div>
  );
}

export default SeoLanding;
